import { copyToClipboard } from './clipboard';
import { userDisplayNameAsString } from './data';

/**
 * Sharing an item or store link from the site (client request 2026-09-18).
 *
 * Customers were receiving bare links and not always knowing what to do with them,
 * especially after a phone conversation. Every share now carries a short message
 * above the link - the same idea as the checkout page's rental quote share
 * (CheckoutPageWithPayment.js's handleQuoteShare), with a blank line between the
 * greeting and the instruction, and the link on its own line after another blank:
 *
 *   Hello! Jane Doe has shared a link with you from Rent Anything Store.
 *
 *   Open the link and select your rental dates to see the exact rental price.
 *
 *   Happy Renting!
 *
 *   https://rentanythingstore.com/rentals/...
 *
 * Where the preview card lands relative to the message is the receiving app's call,
 * not ours: a paste puts it under the message, a direct share to Messages draws it
 * first. See shareOrCopyLink for how the link is carried and why.
 *
 * The sender is the signed-in user's first + last name (their display name as a
 * fallback); a signed-out share says "A link has been shared with you". The site is
 * named by its marketplace name rather than its domain - see getShareSiteName.
 */

export const SHARE_OUTCOME_SHARED = 'shared';
export const SHARE_OUTCOME_COPIED = 'copied';
export const SHARE_OUTCOME_DISMISSED = 'dismissed';
export const SHARE_OUTCOME_FAILED = 'failed';

/**
 * The signed-in sender's name for the message, or '' when there is no usable one.
 * First + last name when both are set (how the quote share names the sender), else
 * the display name.
 *
 * @param {Object|null} currentUser
 * @returns {string}
 */
export const getShareSenderName = currentUser => {
  const profile = currentUser?.attributes?.profile;
  if (!profile) return '';
  const { firstName, lastName } = profile;
  if (firstName && lastName) return `${firstName} ${lastName}`.trim();
  return userDisplayNameAsString(currentUser, '');
};

/**
 * The site's name for the message - "Rent Anything Store".
 *
 * Deliberately the marketplace NAME, not the domain it used to be (client report
 * 2026-09-23): messaging apps linkify anything shaped like a domain, so
 * "rentanythingstore.com" arrived as a second tappable link competing with the
 * listing link the message is actually about. A name can't be mistaken for a URL,
 * and it matches how the rental quote names its store.
 *
 * Callers pass `config.marketplaceName`; the env var behind it is the fallback for
 * the odd caller with no config at hand.
 *
 * @param {string} [configuredName] - config.marketplaceName
 * @returns {string}
 */
export const getShareSiteName = configuredName =>
  configuredName || process.env.REACT_APP_MARKETPLACE_NAME || '';

/**
 * The page's own URL, without the query string or hash - the canonical listing or
 * store address, which is all a recipient needs.
 *
 * A listing reached from search carries the whole search state in its URL
 * (`?from=search&queryID=...&searchUrl=...`), which turned the shared link into a
 * screenful of encoded text in the recipient's message (client screen recording
 * 2026-09-26). Those params exist for our own back-navigation and analytics; none of
 * them mean anything to the person receiving the link.
 *
 * @returns {string}
 */
export const getShareableUrl = () => {
  if (typeof window === 'undefined' || !window.location) return '';
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`;
};

/**
 * The message shown above the link. Two paragraphs separated by a blank line.
 *
 * @param {Object} intl
 * @param {{senderName?: string, siteName?: string}} options - siteName is
 *   config.marketplaceName
 * @returns {string}
 */
export const buildShareLinkMessage = (intl, { senderName = '', siteName = '' } = {}) => {
  const site = getShareSiteName(siteName);
  const greeting = senderName
    ? intl.formatMessage({ id: 'ShareLink.greetingWithSender' }, { senderName, siteName: site })
    : intl.formatMessage({ id: 'ShareLink.greetingNoSender' }, { siteName: site });
  const instructions = intl.formatMessage({ id: 'ShareLink.instructions' });
  const signOff = intl.formatMessage({ id: 'ShareLink.signOff' });
  return `${greeting}\n\n${instructions}\n\n${signOff}`;
};

/**
 * The link is sent BOTH ways at once: inside the text as its last line, and as the
 * share's `url`. That looks redundant and is not - the message and the link travel as
 * two separate items, and every app helps itself to a different one (client screen
 * recording 2026-09-26):
 *
 *   Apple Messages, Mail    read both        - needed `url` for the preview card,
 *                                              which plain text alone never produces
 *                                              (text a share sheet inserts is not
 *                                              scanned for links; a paste is).
 *   Slack's share extension takes `url`      - and silently discards the message.
 *   Grasshopper, and Slack  take plain text  - dropping a separately-carried link, so
 *   on paste                                   the message arrived with nothing to tap.
 *
 * Carrying the link in both items is the only shape that leaves every one of them with
 * a usable message: whichever item an app reads, the link is in it. It is what the
 * checkout page's quote share has always done, which is why the quote kept working in
 * Grasshopper while the listing share did not.
 *
 * The cost is a link shown twice by an app that reads both items, and that is the
 * lesser evil against a customer receiving a message with no link at all.
 *
 * Setting SHARE_LINK_AS_ATTACHMENT to false drops the `url` item: Slack's share
 * extension would then keep our message, at the price of no preview card in Messages.
 * Shipped that way on 2026-09-24 and reverted the same day - the card is worth more.
 */
const SHARE_LINK_AS_ATTACHMENT = true;

/**
 * Hands `message` + `url` to the native share sheet where there is one (phones,
 * Safari), else copies the same text to the clipboard.
 *
 * Resolves with one of the SHARE_OUTCOME_* values - the caller shows "Link copied"
 * feedback only for the copy path; a dismissed share sheet is not an error.
 *
 * @param {{title: string, message: string, url: string}} params
 * @returns {Promise<string>}
 */
export const shareOrCopyLink = async ({ title, message, url }) => {
  // The link is part of the text on every path, so an app that reads only text still
  // has something to tap.
  const textWithUrl = message ? `${message}\n\n${url}` : url;
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    const attachmentPayload = { title, text: textWithUrl, url };
    const canShareUrl =
      typeof navigator.canShare === 'function' ? navigator.canShare(attachmentPayload) : true;
    const payload =
      SHARE_LINK_AS_ATTACHMENT && canShareUrl ? attachmentPayload : { title, text: textWithUrl };
    try {
      await navigator.share(payload);
      return SHARE_OUTCOME_SHARED;
    } catch (e) {
      // AbortError: the user closed the sheet. Anything else (e.g. the sheet isn't
      // allowed for this page) falls through to the clipboard so the tap still
      // does something.
      if (e?.name === 'AbortError') return SHARE_OUTCOME_DISMISSED;
    }
  }
  try {
    await copyToClipboard(textWithUrl);
    return SHARE_OUTCOME_COPIED;
  } catch (e) {
    return SHARE_OUTCOME_FAILED;
  }
};
